import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import Link from 'next-translate-routes/link';
import { useRouter } from 'next-translate-routes/router';
import type { FC } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import { Container } from '@/components';
import { DepositsPagination } from '@/components/DepositsPagination';
import { useApi } from '@/contexts/ApiContext';
import BankLayoutHero from '@/layouts/BankLayout/parts/BankLayoutHero';
import { BanksListItem } from '@/layouts/BanksListLayout/parts/BanksListItem';
import type { BanksPageProps } from '@/pages/banks';
import { getCountryFlagURL } from '@/services/supabase';
import { cn } from '@/utils';
import { getBanksBreadcrumbs } from '@/utils/breadcrumbs';
import { COUNTRIES_PATHS } from '@/utils/countries';
import type { ProductsMeta } from '@/utils/products';

export const BanksListLayout: FC<BanksPageProps> = ({
  banks,
  initialMeta,
  country,
  countries,
}) => {
  const { t } = useTranslation();
  const router = useRouter();

  const [localMeta, setLocalMeta] = useState<ProductsMeta>(initialMeta);
  const [localBanks, setLocalBanks] = useState(banks);

  const { getBanksListRequest } = useApi();
  const banksRef = useRef(null);

  const executeScroll = () => {
    setTimeout(() => {
      // @ts-expect-error
      banksRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  };

  const fetchBanks = async (page: number) => {
    try {
      const { data: fetchedBanks, meta: fetchedMeta } =
        await getBanksListRequest(router.locale || 'en', {
          page,
          countries: country ? [country.alpha2] : undefined,
        });
      setLocalBanks(fetchedBanks);
      setLocalMeta(fetchedMeta as any);
    } catch (e) {
      console.log(e);
    }
    executeScroll();
  };

  useEffect(() => {
    setLocalBanks(banks);
    setLocalMeta(initialMeta);
  }, [country, initialMeta, banks]);

  return (
    <div className="relative z-10 flex h-full flex-col rounded-lg">
      <BankLayoutHero
        title={t('banks-page.h1')}
        subtitle={t('banks-page.subtitle')}
        breadcrumbs={getBanksBreadcrumbs(country?.name, router.locale)}
        containerClassName="max-w-[849px]"
      />
      <Container className="relative mt-10 max-w-[849px] lg:px-0">
        <span ref={banksRef} />
        <div className="flex flex-wrap gap-x-2 gap-y-3">
          <Link
            type="button"
            className={cn(
              'rounded-full flex w-max cursor-pointer bg-blue-50 border border-white hover:bg-blue-100 px-3 py-1.5',
              !country?.alpha2 && 'bg-blue-100 border-blue-200',
            )}
            href="/banks/"
            scroll={false}
          >
            <span
              className={cn(
                'body2 flex items-center gap-1',
                !country?.alpha2 && 'text-blue-550',
              )}
            >
              All
            </span>
          </Link>
          {countries.map((c) => (
            <Link
              type="button"
              key={c.alpha2}
              className={cn(
                'rounded-full flex w-max cursor-pointer bg-blue-50 border border-white hover:bg-blue-100 px-3 py-1.5',
                c.alpha2 === country?.alpha2 && 'bg-blue-100 border-blue-200',
              )}
              href={{
                pathname: `/banks/${COUNTRIES_PATHS[c.name][router.locale || 'en']}`,
              }}
              scroll={false}
            >
              <span
                className={cn(
                  'body2 flex items-center gap-1 tracking-[0.2px]',
                  c.alpha2 === country?.alpha2 && 'body2-bold text-blue-550',
                )}
              >
                <Image
                  src={getCountryFlagURL(c.name)}
                  alt={c.name}
                  className="h-3 w-4 rounded-[2px]"
                  width={16}
                  height={12}
                />
                {t(`database.countries.${c.name}`)}
              </span>
            </Link>
          ))}
        </div>
        <ul className="mt-6 flex w-full flex-col divide-y divide-grey-200 border-y border-grey-200">
          {localBanks.map((bank) => {
            if (bank?.country?.name) {
              return <BanksListItem key={bank.uuid} bank={bank} />;
            }
            return null;
          })}
        </ul>
        <DepositsPagination
          page={localMeta.page}
          count={10}
          total={localMeta.total}
          onPageChange={(offset) => {
            router.push(
              {
                pathname: country
                  ? `/banks/${COUNTRIES_PATHS[country.name][router.locale || 'en']}`
                  : '/banks/',
                query: { page: offset },
              },
              undefined,
              { shallow: true },
            );
            fetchBanks(offset);
          }}
          path={
            country
              ? `/banks/${COUNTRIES_PATHS[country.name][router.locale || 'en']}`
              : '/banks/'
          }
          className="mt-6 flex justify-center"
        />
      </Container>
    </div>
  );
};
